import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';

const PostDisplay = ({ text, postType }) => {
  const [showSeeMore, setShowSeeMore] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const textRef = useRef(null);

  
  // Get line limit based on post type
  const getLineClamp = () => {
    if (showFullText) return 100;
    switch(postType) {
      case 'text-only':
        return 3;
      case 'promoted':
        return 2;
      case 'with-media':
      case 'with-link':
      default:
        return 3;
    }
  };
  
  // Check if text should be truncated based on line count
  const shouldTruncate = () => {
    if (showFullText) return false;
    if (!text) return false;
    
    // Count explicit newlines
    const newlineCount = (text.match(/\n/g) || []).length;
    
    // Get line limit for this post type
    const lineLimit = getLineClamp();
    
    // If we already have more explicit newlines than allowed, we need to truncate
    if (newlineCount >= lineLimit) {
      return true;
    }
    
    // Otherwise, rely on the DOM measurement in useEffect
    return false;
  };
  
  // Check if text overflows and we need to show "see more"
  useEffect(() => {
    if (textRef.current && text) {
      const element = textRef.current;
      
      // First check for explicit newlines exceeding our limit
      if (shouldTruncate(text)) {
        setShowSeeMore(true);
        return;
      }
      
      // Then check for overflow due to word wrapping
      if (element.scrollHeight > element.clientHeight + 5) { // Add small tolerance
        setShowSeeMore(true);
      } else {
        setShowSeeMore(false);
      }
    } else {
      setShowSeeMore(false);
    }
  }, [text, postType, showFullText]);

  return (
    <>
      <Box sx={{ position: 'relative', mb: 2, p: 2 }}>
        <Typography 
          ref={textRef}
          variant="body1" 
          sx={{ 
            display: '-webkit-box',
            WebkitLineClamp: getLineClamp(),
            WebkitBoxOrient: 'vertical',
            overflow: 'auto',
            lineHeight: 1.5,
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            whiteSpace: 'pre-line' // This preserves newlines
          }}
          contentEditable={true}
        >
          {text || 'Your LinkedIn post will appear here'}
        </Typography>
        
        {showSeeMore && !showFullText && (
          <Box sx={{ 
            display: 'inline-block', 
            position: 'absolute',
            right: 0,
            bottom: 0,
            bgcolor: 'white',
            pr: 1,
            pl: 0.5
          }}>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ 
                display: 'inline-block', 
                cursor: 'pointer',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}
              onClick={() => {
                setShowSeeMore(false);
                setShowFullText(true);
              }}
            >
              ...see more
            </Typography>
          </Box>
        )}
      </Box>

      {postType === 'with-media' && (
        <Box sx={{ height: 200, bgcolor: '#f5f5f5', mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ImageIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
        </Box>
      )}

      {postType === 'with-link' && (
        <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
          <Box sx={{ height: 120, bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <LinkIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
          </Box>
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2">Link Title Here</Typography>
            <Typography variant="caption" color="text.secondary">example.com</Typography>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default PostDisplay;